import { noop } from 'lodash';
import { useCallback, useState } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { File05Icon } from '@/components/icons/File05Icon';
import { FolderIcon } from '@/components/icons/FolderIcon';
import { FlyoutMenuItem } from '@/components/poppers/MenuPopper/FlyoutMenuItem';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { StandaloneDocumentUploaderModal } from '@/modules/documents/DocumentUploaderModal/StandaloneDocumentUploaderModal';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';
import { PulidKind } from '@/utils/pulid';

import { useHouseholdDocumentsViewerContext } from '../context/HouseholdDocumentsViewer.context';
import { getAdditionalDetailsForPulidKind } from '../HouseholdDocumentsViewer.utils';
import { AddNewDocumentMenuItem } from './AddNewDocumentMenuItem';
import { DocumentSelectorMenuItem } from './DocumentSelectorMenuItem';

export function DocumentSelector() {
  const {
    targetDocuments,
    otherHouseholdDocuments,
    householdId,
    targetId,
    targetKind,
    entityKind,
    refetch,
    updateActiveDocumentId,
    setNewlyUploadedDocumentId,
    hideAddDocument,
  } = useHouseholdDocumentsViewerContext();

  const noDocumentsPresent = !targetDocuments?.length;
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const handleUploadModalClose = useCallback(
    (documentIds: string[]) => {
      setUploadModalOpen(false);

      // Take the first document ID for backward compatibility
      const documentId = documentIds[0];
      if (documentId) {
        refetch();
        updateActiveDocumentId({ documentId, shouldClearSearchResults: true });
        setNewlyUploadedDocumentId(documentId);
      }
    },
    [refetch, updateActiveDocumentId, setNewlyUploadedDocumentId]
  );

  return (
    <>
      <ButtonWithPopover
        size="sm"
        variant="secondary"
        aria-label="Select document"
        popperVariant="menuBelow"
        // Render the menu above SideDrawers (zIndex.drawer === CONTEXTUAL_HELP).
        // MUI menus default to zIndex.modal, which sits *below* the drawer, so
        // the menu would otherwise open behind the drawer and be unclickable.
        popperProps={{ sx: { zIndex: zIndexes.POPOVER } }}
        label={<File05Icon size={16} sx={{ color: COLORS.GRAY[800] }} />}
      >
        {!hideAddDocument && (
          <AddNewDocumentMenuItem onClick={() => setUploadModalOpen(true)} />
        )}
        {otherHouseholdDocuments && otherHouseholdDocuments.length > 0 && (
          <FlyoutMenuItem
            label="Other existing documents"
            icon={
              <FolderIcon size={18} sx={{ color: COLORS.TEXT.SECONDARY }} />
            }
            muiMenuItemProps={{
              divider: true,
            }}
          >
            {otherHouseholdDocuments.map((doc) => (
              <DocumentSelectorMenuItem
                key={doc.id}
                document={doc}
                // hide the "default" button for documents that aren't
                // already directly associated with the entity/te/cp
                allowSetDefault={false}
              />
            ))}
          </FlyoutMenuItem>
        )}
        {targetDocuments?.map((doc) => (
          <DocumentSelectorMenuItem key={doc.id} document={doc} />
        ))}
        {noDocumentsPresent && (
          <MenuItem onClick={noop} label="No documents" disabled />
        )}
      </ButtonWithPopover>
      <StandaloneDocumentUploaderModal
        householdId={householdId}
        entityId={targetKind === PulidKind.Entity ? targetId : undefined}
        entityType={entityKind && getEntityTypeFromEntityKind(entityKind)}
        uploadModalOpen={uploadModalOpen}
        onModalClose={handleUploadModalClose}
        additionalDetails={getAdditionalDetailsForPulidKind(targetKind)}
        // Render above SideDrawers (zIndex.drawer); the uploader can be opened
        // from the document selector inside the suggestion evidence drawer.
        zIndex={zIndexes.POPOVER}
      />
    </>
  );
}
